import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../lib/cn";
import { RedlineSpan } from "../primitives/RedlineSpan";

/**
 * DiffRow — single-row diff view for inline change lists. The left cell shows
 * the removed text, the right cell the added text. Optional `meta` line
 * appears under the diff (anchor / clause reference / context note), and an
 * optional `verdict` slot floats on the right edge for status badges or
 * accept/reject controls.
 *
 * Designed for "show me a long list of small changes" surfaces — counter-
 * redline review, version diff side-rail, audit-log entries that compare
 * before/after. Not a general-purpose word-level differ; the consumer is
 * expected to pass already-segmented strings.
 *
 * Composition: each cell renders its text through `RedlineSpan` (the
 * `deletion` / `insertion` variants) so the strikethrough + ins/del coloring
 * stay consistent with inline redlines elsewhere in the document. The cell
 * `<div>` carries the bordered, tinted container chrome.
 *
 * Token reads: `--del-bg` for the removed cell tint, `--ins-bg` for the added
 * cell tint, `--del` / `--ins` for the foreground text colors (via the
 * composed `RedlineSpan`). Falls back through the diff-token defaults so
 * consumers without `tokens.template.css` still get a working palette.
 *
 * Motion: the only animated property is the interactive `transition-colors`,
 * gated with `motion-reduce:transition-none` so reduced-motion users get an
 * instant hover state.
 */
const diffRowVariants = cva(
  "grid grid-cols-[1fr_1fr] gap-3 rounded-lg border border-border bg-card p-3 text-sm",
  {
    variants: {
      density: {
        comfortable: "p-3 gap-3",
        compact: "p-2 gap-2 text-xs",
      },
    },
    defaultVariants: { density: "comfortable" },
  },
);

const cellVariants = cva("rounded-md border px-2.5 py-1.5", {
  variants: {
    side: {
      removed: "border-del/30 bg-del-bg/60",
      added: "border-ins/30 bg-ins-bg/60",
    },
  },
});

export interface DiffRowProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children">,
    VariantProps<typeof diffRowVariants> {
  /** Original text (the left cell). */
  removed: string;
  /** New text (the right cell). */
  added: string;
  /** Optional meta line below the diff — usually a clause anchor or context note. */
  meta?: React.ReactNode;
  /** Optional verdict slot — accept/reject buttons, status badge, etc. */
  verdict?: React.ReactNode;
  /** Optional click handler for the entire row (e.g. scroll-to-clause). */
  onClick?: () => void;
}

export const DiffRow = React.forwardRef<HTMLDivElement, DiffRowProps>(
  ({ className, density, removed, added, meta, verdict, onClick, ...props }, ref) => {
    const isInteractive = !!onClick;
    return (
      <div
        ref={ref}
        role={isInteractive ? "button" : undefined}
        tabIndex={isInteractive ? 0 : undefined}
        onClick={onClick}
        onKeyDown={
          isInteractive
            ? e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        className={cn(
          diffRowVariants({ density }),
          isInteractive &&
            "cursor-pointer transition-colors motion-reduce:transition-none hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
        {...props}
      >
        <div className={cn(cellVariants({ side: "removed" }))}>
          <RedlineSpan variant="deletion" className="bg-transparent">
            {removed}
          </RedlineSpan>
        </div>
        <div className={cn(cellVariants({ side: "added" }))}>
          <RedlineSpan variant="insertion" className="bg-transparent">
            {added}
          </RedlineSpan>
        </div>
        {(meta || verdict) && (
          <div className="col-span-2 mt-1 flex items-center justify-between gap-3 text-xs text-muted-foreground">
            <span className="min-w-0 truncate">{meta}</span>
            {verdict ? <span className="shrink-0">{verdict}</span> : null}
          </div>
        )}
      </div>
    );
  },
);
DiffRow.displayName = "DiffRow";

export { diffRowVariants };
